<template>
  <div v-if="loading" class="app-loading">
    <q-spinner-dots color="primary" size="60px" />
  </div>
  <router-view v-else />
</template>

<script>
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useAuthLoginStore } from 'modules/authentication/stores/auth-login-store'
import useAuth from 'modules/authentication/composables/useAuth'
import {
  attachActivityListeners,
  hasRecentActivity
} from 'modules/authentication/utilities/auth-activity'

export default defineComponent({
  name: 'App',
  setup() {
    const { checkAuthStatus } = useAuth()
    const authStore = useAuthLoginStore()
    const loading = ref(true)
    let intervalId = null

    // Esperá el resultado real antes de quitar el spinner
    checkAuthStatus().finally(() => {
      loading.value = false
    })

    // El watch cubre el caso donde el status cambia por otra vía (ej: logout externo)
    const unwatch = watch(
      () => authStore.currentStatus,
      (status) => {
        if (status === 'authenticated' || status === 'not-authenticated') {
          loading.value = false
          unwatch()
        }
      }
    )

    onMounted(() => {
      attachActivityListeners()

      const TEST_MODE = true          // ← poner false para producción
      const INTERVAL_MS = TEST_MODE ? 3 * 60_000 : 15_000

      intervalId = setInterval(async () => {
        if (authStore.currentStatus !== 'authenticated') return
        if (document.visibilityState !== 'visible') return

        const activity = hasRecentActivity(3 * 60 * 1000) // actividad en el último minuto

        if (TEST_MODE) {
          // ── TEST: refrescar directo si hay actividad, sin chequear expiración ──
          if (activity) {
            console.debug('[Auth] TEST refresh triggered by activity')
            await authStore.refreshSession()
          }
          return
        }

        // ── PRODUCCIÓN ──────────────────────────────────────────────────────────
        if (!authStore.expValue) return

        const msLeft = authStore.getMsUntilExpiry()

        if (msLeft <= 2 * 60 * 1000 && msLeft > 60 * 1000) {
          await authStore.maybeRefreshSilently(activity)
        }

        if (msLeft <= 60 * 1000 && msLeft > 0 && !activity) {
          authStore.showRefreshModal = true
        }
      }, INTERVAL_MS)
    })

    onBeforeUnmount(() => {
      if (intervalId) clearInterval(intervalId)
    })

    return { loading }
  }
})
</script>

<style scoped>
.app-loading {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  z-index: 9999;
}
</style>